import { scanContent } from '@seek/content-safety-validator';
import type { Country } from '@seek/content-safety-validator/lib-types/types';
import type { Language } from '@seek/melways-sites';
import {
  createValidator,
  runValidation,
  type Validator,
  type FieldProps,
} from '@seek/validators-js';

import { countEmoji } from 'src/app/utils/emoji';
import { getCharacterCount } from 'src/app/utils/getCharacterCount';
import type {
  WarningKeyword,
  WarningKeywordType,
} from 'src/app/v1/dataAnalytics/utils/warningKeyword';

import translations from './.vocab';

type ValidationResult = string | undefined;

type ValidationFunction = (...params: any[]) => ValidationResult;

type DataObject = Record<string, any>;

type Rules = Record<string, Rule | Rule[]>;

export type Rule = (
  value: string,
  data: DataObject,
  params: DataObject,
) => ValidationResult;

type Fields = Parameters<typeof runValidation>[0]['fields'];
export type Validators = Parameters<
  typeof runValidation<Fields, Language>
>[0]['rules'][number]['validators'];

const isEmpty = (value: string | number) => !(value || '').toString().trim();

const mergeRules =
  (rules: Rule[]) => (value: string, data: DataObject, params: DataObject) =>
    rules
      .map((rule) => rule(value, data, params))
      .find((error) => Boolean(error));

/**
 * Validates that a value is not empty.
 * @deprecated Replace with validators-js required
 * @param message - The message to return when the value is invalid.
 */
export const required =
  (message = 'Required'): ValidationFunction =>
  (value: string) =>
    isEmpty(value) ? message : undefined;

/**
 * Validates that a value is at least the specified number of characters.
 * @deprecated Replace with validators-js minLength
 * @param {number} min - The minimum number of characters.
 * @param message - The message to return when the value is invalid.
 * @param sanitize - True if html should be stripped before counting.
 */
export const minLength =
  (
    min: number,
    message = `Must be at least ${min} characters`,
  ): ValidationFunction =>
  (value: string) =>
    !isEmpty(value) && getCharacterCount(value) < min ? message : undefined;

/**
 * Validates that a value is no more than the specified number of characters.
 * @deprecated Replace with validators-js maxLength
 * @param {number} max - The maximum number of characters.
 * @param message - The message to return when the value is invalid.
 * @param sanitize - True if html should be stripped before counting.
 */
export const maxLength =
  (
    max: number,
    message = `Must be no more than ${max} characters`,
  ): ValidationFunction =>
  (value: string) =>
    !isEmpty(value) && getCharacterCount(value) > max ? message : undefined;

/**
 * Validates that a value matches a valid YouTube URL.
 * @param message - The message to return when the value is invalid.
 */

export const { youTubeUrl } = createValidator({
  test: ({ value }: { value: string }) => {
    if (!Boolean(value)) {
      return true;
    }

    const watchLinkRegex = /\/\/www\.youtube\.com\/watch/;
    const embedLinkRegex = /\/\/www\.youtube\.com\/embed/;
    const shortsLinkRegex = /\/\/www\.youtube\.com\/shorts/;
    const shortLinkRegex = /\/\/youtu.be\//;

    return (
      Boolean(value.match(watchLinkRegex)) ||
      Boolean(value.match(shortLinkRegex)) ||
      Boolean(value.match(shortsLinkRegex)) ||
      Boolean(value.match(embedLinkRegex))
    );
  },
  formatErrorMessages: (messages) => () =>
    messages['youtube-url-validation-message'].format(),
  translations,
  validatorName: 'youTubeUrl',
});

interface InvalidTextRange {
  start: number;
  end?: number;
}

export type InvalidText = InvalidTextRange[] | null;
export type FormValues = Record<string, any>;
export interface SendTrackingEvents {
  formValues: FormValues;
  jobCountry: Country;
  message: string;
  warningKeyword: WarningKeyword;
  warningKeywordType: WarningKeywordType;
}

type FieldPropsType = FieldProps<
  string,
  {
    country: Country;
    sendTrackingEvents?: ({
      jobCountry,
      formValues,
      message,
      warningKeyword,
      warningKeywordType,
    }: SendTrackingEvents) => void;
  },
  FormValues
>;

export const { filterContent } = createValidator({
  test: ({ value, validatorProps: { country } }: FieldPropsType) => {
    const result = scanContent(value, { country });
    const hasRestrictedWords = Object.keys(result.restricted).length > 0;
    return !hasRestrictedWords;
  },
  formatErrorMessages:
    (messages) =>
    ({
      value,
      validatorProps: { country, sendTrackingEvents = () => {} },
      formValues,
    }: FieldPropsType) => {
      const result = scanContent(value, { country });
      const restrictedWords = Object.keys(result.restricted).sort(
        (word1, word2) => {
          const rangeForWord1 = result.restricted[word1];
          const rangeForWord2 = result.restricted[word2];
          return rangeForWord1[0].start - rangeForWord2[0].start;
        },
      );

      const separator = messages['restricted-words-separator'].format();
      const restrictedWordsText = restrictedWords.join(separator);

      const message =
        restrictedWords.length === 1
          ? messages['Restricted word'].format({
              badWord: restrictedWordsText,
            })
          : messages['Restricted words'].format({
              badWords: restrictedWordsText,
            });

      sendTrackingEvents({
        jobCountry: country,
        formValues,
        message,
        warningKeyword: restrictedWords,
        warningKeywordType: 'restricted',
      });

      return message;
    },
  translations,
  getInvalidText: ({ value, validatorProps: { country } }: FieldPropsType) => {
    const result = scanContent(value, { country });
    const restrictedWords: InvalidText = Object.values(result.restricted)
      .flat()
      .map((range) => ({
        ...range,
      }));
    if (restrictedWords.length > 0) {
      return restrictedWords;
    }

    return null;
  },
  validatorName: 'filterContent',
});

/**
 * Validates that a value matches a valid country phone number.
 * @param country - The country to validate the value against.
 * @return untranslated custom error message when invalid
 */

interface CountryPhoneProps {
  country: string;
}

export const { countryPhone } = createValidator({
  test: ({
    value,
    validatorProps,
  }: {
    value: string;
    validatorProps: CountryPhoneProps;
  }) => {
    // To be updated when we support more countries in future
    const { country } = validatorProps;
    const phoneRegex: Record<string, RegExp> = {
      AU: /(^((\+614)|(0[23478]))\d{8}$)|(^((1300)|(1800))\d{6}$)/g,
      NZ: /(^02\d{7,9}$)|(^(3|4|6|7|9)\d{7}$)|(^(03|04|06|07|09)\d{7}$)|(^(021|022|025|027|028|029)\d{6,8}$)|(^(0508|0800|0900)\d{5,8}$)/g,
    };
    const defaultRegex = /(.*[0-9]){6}/;
    return phoneRegex.hasOwnProperty(country)
      ? phoneRegex[country].test(value.replace(/[^0-9+]/g, ''))
      : defaultRegex.test(value.replace(/[^0-9+]/g, ''));
  },
  formatErrorMessages: (messages) => () =>
    messages['country-phone-validation-message'].format(),
  translations,
  validatorName: 'countryPhone',
});

interface MaxEmojiLengthValidatorProps {
  maxEmojiLength?: number;
}

export const MAX_EMOJI_LENGTH_VALIDATOR_NAME = 'maxEmojiLengthValidator';
export const { maxEmojiLengthValidator } = createValidator({
  test: ({
    value,
    validatorProps,
  }: {
    value: string;
    validatorProps: MaxEmojiLengthValidatorProps;
  }) => {
    const { maxEmojiLength } = validatorProps;

    if (!maxEmojiLength) {
      return true;
    }

    const emojiLength = countEmoji(value);
    return emojiLength <= maxEmojiLength;
  },
  formatErrorMessages: (messages) => () =>
    messages['max-emoji-length-validation-message'].format(),

  translations,
  validatorName: MAX_EMOJI_LENGTH_VALIDATOR_NAME,
});

/**
 * Validates that a value is not empty.
 * @return translated custom error message when invalid
 */
export const customRequired: Validator<string> = {
  test: ({ value }) =>
    Boolean(value && value.length > 0) && value.trim() !== '',
  getErrorMessage: ({ validatorProps }: any) =>
    Promise.resolve(validatorProps.requiredMessage),
  getErrorMessageSync: ({ validatorProps }: any) =>
    validatorProps.requiredMessage,
  name: 'required',
};

/**
 * Creates a validate method to be used for validating an object.
 * @param rules - One or more rules to use to validate an object.
 * @param {DataObject} params - Additional parameters to pass to the rules.
 * @example
 *
 * import { createValidator, required, minLength, maxLength, regex } from 'src/utils/validation/validation';
 * const validate = createValidator({
 *   field: [
 *     required('Please enter a field value'),
 *     minLength(4, 'The field value needs to be between 4 and 80 characters'),
 *     maxLength(80, 'The field value needs to be between 4 and 80 characters'),
 *     regex(/([ -~]|[–|—])+/, 'Only printable characters are allowed'),
 *   ],
 * });
 *
 * const validationResult = validate({ field: 'Field value text' });
 * validationResult => {} // is valid
 */
export const createCustomValidator =
  (rules: Rules, params?: DataObject) =>
  /**
   * Validates an object against a set of rules.
   * @deprecated Replace with validateField()
   * @param obj - The object to validate.
   * @param skipIfNull - A value indicating whether to validate the value against null value
   * @returns {{}} - An object of errors or an empty object if all keys are valid.
   */
  (obj: DataObject = {}, skipIfNull = false) =>
    Object.keys(rules).reduce((errors: any, key) => {
      if (!skipIfNull || obj[key]) {
        const getFirstError = mergeRules(
          (Array.isArray(rules[key]) ? rules[key] : [rules[key]]) as Rule[],
        );
        errors[key] = getFirstError(obj[key], obj, { key, ...params }) || null;
      }
      return errors;
    }, {});

interface ValidatorOptions {
  field: string;
  validators: Validator<any, any, Record<string, any>, Language>[];
  validatorProps: any;
  value: string | boolean | number;
}

type ValidationRules = Array<ValidatorOptions>;
export type ValidateFieldResult = string | null;

export type TrackingFnProps = {
  validationResult: ReturnType<typeof runValidation>;
  fieldName: string;
};
type trackingFn = (props: TrackingFnProps) => void;

/**
 * Run validators-js runValidation function against options provided.
 * @param options - The field, value, validators and validatorProps to use.
 * @param skipIfNull - A value indicating whether to validate the value against null value
 * @returns {{}} - An object of errors or an empty object if all keys are valid.
 */
export const validateField = (
  options: ValidatorOptions,
  skipIfNull = false,
  language: Language = 'en',
  trackingFns: trackingFn[] = [],
): ValidateFieldResult => {
  const { field, validators, validatorProps, value } = options;
  const newValidators = skipIfNull
    ? validators.filter((validator) => validator.name !== 'required')
    : validators;

  const fields = {
    [field]: value,
  };
  const rules = {
    [field]: {
      validators: newValidators,
      validatorProps,
    },
  };
  const validationResult = runValidation<typeof fields, Language>(
    { fields, rules },
    language,
  );

  let validationResultMessage = null;

  if (validationResult && !validationResult.valid) {
    validationResultMessage = validationResult.errors[0].message;
  }

  trackingFns.forEach((trackingFn) =>
    trackingFn({ validationResult, fieldName: field }),
  );

  return validationResultMessage;
};

type RunValidationObject = {
  fields: Record<string, string | number | boolean>;
  rules: Record<
    string,
    {
      validators: Array<Validator<any, any, Record<string, any>, Language>>;
      validatorProps?: Record<string, any>;
    }
  >;
  language?: Language;
};

/**
 * Run validators-js runValidation function against options provided.
 * @param validationRules - An object containing the field name, the fields' value, validators and validatorProps to use.
 * @param skipIfNull - A value indicating whether to validate the value against null value
 * @returns {{}} - An object of fields, and displayed error string if any.
 */

export const validateFields = (
  validationRules: ValidationRules,
  skipIfNull = false,
  language: Language = 'en',
  trackingFns: trackingFn[] = [],
): Record<string, ValidationResult> => {
  if (skipIfNull) {
    validationRules.forEach((validationRule) => {
      validationRule.validators.filter(
        (validator) => validator.name !== 'required',
      );
    });
  }

  const validationValues = validationRules.reduce(
    (result, curr) => {
      result.fields[curr.field] = curr.value;

      if (!result.rules[curr.field]) {
        result.rules[curr.field] = { validators: [] };
      }

      result.rules[curr.field].validators = curr.validators;
      result.rules[curr.field].validatorProps = curr.validatorProps;
      result.language = language;

      return result;
    },
    {
      fields: {},
      rules: {},
      language,
    } as RunValidationObject,
  );

  const validationResult = runValidation<
    typeof validationValues.fields,
    Language
  >(
    {
      fields: validationValues.fields,
      rules: validationValues.rules,
    },
    language,
  );

  const fieldRecord: Record<string, ValidationResult> = {};
  if (validationResult || skipIfNull) {
    Object.keys(validationValues.fields).map((fieldName) => {
      const errorMessage = validationResult.errors.find(
        (error) => error.fieldName === fieldName,
      )?.message;
      fieldRecord[fieldName] = errorMessage;
    });
  }
  trackingFns.forEach((trackingFn) =>
    Object.keys(validationValues.fields).map((fieldName) => {
      trackingFn({ validationResult, fieldName });
    }),
  );

  return fieldRecord;
};
